import React from 'react';
import { Eye, AlertTriangle, X } from 'lucide-react';

interface LegalModalsProps {
  showPrivacyModal: boolean;
  setShowPrivacyModal: (show: boolean) => void;
  showDisclaimerModal: boolean;
  setShowDisclaimerModal: (show: boolean) => void;
}

export const LegalModals: React.FC<LegalModalsProps> = ({
  showPrivacyModal,
  setShowPrivacyModal,
  showDisclaimerModal,
  setShowDisclaimerModal,
}) => {
  return (
    <>
      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" 
          onClick={() => setShowPrivacyModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl max-h-[80vh] overflow-y-auto p-8 shadow-2xl border border-slate-100" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
              <h2 className="text-2xl font-medium flex items-center gap-2 text-slate-900">
                <Eye className="w-6 h-6 text-[#06B6D4]" /> Privacy Policy
              </h2>
              <button 
                onClick={() => setShowPrivacyModal(false)} 
                className="p-2 hover:bg-slate-100 text-slate-500 hover:text-slate-900 rounded-full transition-colors"
                aria-label="Close Privacy Modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="prose prose-sm text-[#6B7280] space-y-4 leading-relaxed">
              <p className="text-xs text-slate-400 font-mono"><strong>Last Updated:</strong> March 30, 2026</p>
              <h3 className="text-lg font-medium text-[#0B0C0F]">1. Information We Collect</h3>
              <p>UV Pulser, an aFoundry venture, collects information you provide directly to us, including name, email address, organization, and inquiry details when you contact us or express investment interest.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">2. How We Use Your Information</h3>
              <p>We use your information to respond to inquiries, process investment expressions, improve our products, and communicate updates about UV Pulser technology.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">3. Data Security & Bot Protection</h3>
              <p>We implement industry-standard cryptographic security measures including Cloudflare Turnstile token validation, CSRF protection, rate limiting, and encrypted transmissions. Your data is stored securely in audited cloud databases and never sold to third parties.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">4. Your Rights</h3>
              <p>You have the right to access, correct, or delete your personal information. Contact us at juan@afoundry.ai for any verified data requests.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">5. Global Compliance</h3>
              <p>This privacy policy complies with GDPR (EU), CCPA (California), PDPA (Singapore), and other applicable data protection regulations worldwide.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">6. Contact</h3>
              <p>For privacy-related questions, contact our data protection officer through the contact form on this website or at juan@afoundry.ai.</p>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" 
          onClick={() => setShowDisclaimerModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl max-h-[80vh] overflow-y-auto p-8 shadow-2xl border border-slate-100" 
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
              <h2 className="text-2xl font-medium flex items-center gap-2 text-slate-900">
                <AlertTriangle className="w-6 h-6 text-[#06B6D4]" /> Disclaimer
              </h2>
              <button 
                onClick={() => setShowDisclaimerModal(false)} 
                className="p-2 hover:bg-slate-100 text-slate-500 hover:text-slate-900 rounded-full transition-colors"
                aria-label="Close Disclaimer Modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="prose prose-sm text-[#6B7280] space-y-4 leading-relaxed">
              <p className="text-xs text-slate-400 font-mono"><strong>Last Updated:</strong> March 30, 2026</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">1. General Information</h3>
              <p>The information provided on uvpulser.com is for general informational purposes only. UV Pulser, an aFoundry venture, makes no representations or warranties of any kind about the completeness, accuracy, reliability, or availability of the information.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">2. Product Claims & Engineering Specifications</h3>
              <p>While we strive for accuracy, product specifications and performance claims are subject to ongoing refinement and laboratory certification. Actual performance may vary based on water turbidity, flow velocity, usage conditions, and system maintenance.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">3. Investment Information</h3>
              <p>Investment opportunities mentioned on this website are subject to regulatory compliance and accredited investor verification. Nothing on this website constitutes investment advice or an offer to sell securities in any jurisdiction where prohibited.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">4. Limitation of Liability</h3>
              <p>UV Pulser and aFoundry shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of this website or our products.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">5. Intellectual Property</h3>
              <p>All content, logos, trademarks, schematic diagrams, and patent disclosures displayed on this website are the property of UV Pulser or aFoundry and are protected by international intellectual property treaties.</p>
              
              <h3 className="text-lg font-medium text-[#0B0C0F]">6. Governing Law</h3>
              <p>This disclaimer is governed by the laws of Singapore, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Singapore.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
